// Lib
import { get, isEmpty, isEqual } from 'lodash';
import Handsontable from 'handsontable/base';
import { useCallback } from 'react';

// Utils
import { getCellValueArray } from '../utils/tableDataUtils';
import { asObject } from '../../../../common/utils/immutableHelper';
import { convertColWidthsGUtoPx, getColWidthsResizeWidth } from '../utils/tableSizeUtils';
import { repositionAutofillHandle } from '../utils/tableCellSelectionUtils';
import MilanoteEditingPlugin, { MILANOTE_EDITING_PLUGIN_NAME } from '../modules/MilanoteEditingPlugin';
import { updateHotTableCellSelections } from '../utils/hotInstanceUtils/hotTableUpdateCellSelectionsUtils';

// Types
import { CellSelections, TableContentData } from '../../../../common/table/TableTypes';
import { MNElement } from '../../../../common/elements/elementModelTypes';
import { setHotTableCellMetaObject } from '../utils/hotInstanceUtils/hotTableUpdateUtils';

interface UseTableStateSyncProps {
    element: MNElement;
    mounted: boolean;
    tableContentData: TableContentData;
    currentCellSelections: CellSelections;
    tableContentColWidthsGU: number[];

    locale: string;
    inList: string | null;
    gridSize: number;
    isGridMoving: boolean;
    isPresentational: boolean;
    isUndoingOrRedoing: boolean;
    isSingleSelected: boolean;

    setIsGridMoving: (isGridMoving: boolean) => void;
    getContextZoomScale: () => number;

    hotTableContainerRef: React.MutableRefObject<HTMLDivElement | null>;
    hotTableInstanceRef: React.MutableRefObject<Handsontable.Core | null>;

    dispatchUpdateTableElement: (args: object) => void;
}

interface UseTableStateSyncRef {
    syncTableData: () => void;
    syncCellSelections: () => void;
    syncColWidths: () => void;
}

/**
 * This hook is used to sync any changed table data from the Redux state, and apply it to the Handsontable instance
 * In general, we try to do all updates via the handsontable state, and then sync the redux state to the handsontable state
 * This hook covers the scenario where the redux state is updated, and we need to sync the handsontable state e.g.
 * - Initialisation
 * - Undo/Redo
 * - Remote changes
 */
const useTableStateSync = (props: UseTableStateSyncProps): UseTableStateSyncRef => {
    const {
        tableContentData,
        currentCellSelections,
        tableContentColWidthsGU,
        gridSize,
        inList,
        getContextZoomScale,
        isGridMoving,
        isSingleSelected,
        setIsGridMoving,

        hotTableContainerRef,
        hotTableInstanceRef,
    } = props;

    /*******************
     * Table data
     *******************/

    const syncTableData = useCallback(() => {
        if (!hotTableInstanceRef.current) return;
        if (isGridMoving) setIsGridMoving(false);

        const milanoteEditingPluginInstance = hotTableInstanceRef.current.getPlugin(
            MILANOTE_EDITING_PLUGIN_NAME,
        ) as MilanoteEditingPlugin;
        if (!milanoteEditingPluginInstance) return;

        const cellValuesFromHot = milanoteEditingPluginInstance.getOriginalSourceDataArray();
        const cellValuesFromRedux = getCellValueArray(tableContentData);

        if (!isEqual(cellValuesFromRedux, cellValuesFromHot)) {
            hotTableInstanceRef.current.updateData(cellValuesFromRedux);
        }

        // Evaluate if any cell data has changed
        const changes: Array<[number, number, Handsontable.CellValue]> = [];
        cellValuesFromRedux.forEach((rowData, row) => {
            rowData.forEach((_, col) => {
                if (!hotTableInstanceRef.current) return;

                const cellData = get(tableContentData, [row, col]);
                const { cellData: hotCellData } = hotTableInstanceRef.current.getCellMeta(row, col);

                if (!isEqual(hotCellData, cellData)) {
                    setHotTableCellMetaObject(hotTableInstanceRef.current, row, col, { cellData });

                    changes.push([row, col, cellData?.value]);
                }
            });
        });

        repositionAutofillHandle(hotTableInstanceRef.current, hotTableContainerRef);

        if (!isEmpty(changes)) {
            hotTableInstanceRef.current.render();

            // This is required to ensure that the table is resized properly for the new content
            requestAnimationFrame(() => {
                if (!hotTableInstanceRef.current || hotTableInstanceRef.current.isDestroyed) return;

                // There is a Handsontable bug where setDataAtCell only resolves source properly if passed as 4th param,
                // despite the type overloads saying otherwise
                // TODO-HANDSONTABLE-UPGRADE: Check whether this is still a bug after upgrading Handsontable
                hotTableInstanceRef.current.setDataAtCell(changes, undefined, undefined, 'CellMeta.init');
            });
        }
    }, [tableContentData, isGridMoving, setIsGridMoving, hotTableContainerRef]);

    /*******************
     * Cell Selections
     *******************/

    const syncCellSelections = useCallback(() => {
        if (!hotTableInstanceRef.current) return;

        if (!isSingleSelected) return;

        const currentHotCellSelections = hotTableInstanceRef.current.getSelected();
        if (isEqual(asObject(currentCellSelections), currentHotCellSelections)) return;

        updateHotTableCellSelections(hotTableInstanceRef.current, currentCellSelections);
    }, [hotTableInstanceRef, currentCellSelections, isSingleSelected]);

    /*******************
     * Column Widths
     *******************/

    const updateColWidthPx = useCallback(
        (column: number, newColWidthPx: number): void => {
            if (!hotTableInstanceRef.current) return;

            const manualColumnResizePluginInstance =
                hotTableInstanceRef.current.getPlugin<'manualColumnResize'>('manualColumnResize');
            const newSize = manualColumnResizePluginInstance.setManualSize(column, newColWidthPx);

            // This is a hack to force the internal state of the manualColumnResize plugin to contain the size
            // that corresponds to our grid unit
            manualColumnResizePluginInstance.newSize = newSize;
        },
        [hotTableInstanceRef],
    );

    const updateColWidthsPx = useCallback(
        (newColWidthsPx: number[]): void => {
            if (!hotTableInstanceRef.current) return;

            let isUpdated = false;
            newColWidthsPx.forEach((colWidthPx, index) => {
                const currColWidth = hotTableInstanceRef.current?.getColWidth(index);

                if (currColWidth === colWidthPx) return;

                updateColWidthPx(index, colWidthPx);
                isUpdated = true;
            });

            if (isUpdated)
                requestAnimationFrame(() => {
                    if (!hotTableInstanceRef.current || hotTableInstanceRef.current.isDestroyed) return;

                    hotTableInstanceRef.current.render();
                });
        },
        [hotTableInstanceRef, updateColWidthPx],
    );

    // Update col widths px in hot table based on container size
    const syncColWidths = useCallback(() => {
        const resizeToWidth = getColWidthsResizeWidth(inList, hotTableContainerRef, getContextZoomScale);
        const tableContentColWidthsPx = convertColWidthsGUtoPx(tableContentColWidthsGU, gridSize, resizeToWidth);

        if (!tableContentColWidthsPx) return;

        updateColWidthsPx(tableContentColWidthsPx);
    }, [tableContentColWidthsGU, gridSize, inList, updateColWidthsPx]);

    return { syncTableData, syncCellSelections, syncColWidths };
};

export default useTableStateSync;
